import { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useDormitory, Dormitory, DormRoom } from '../../context/DormitoryContext';

const colorMap: Record<string, { badge: string; btn: string; tag: string; accent: string }> = {
  teal: {
    badge: 'bg-teal-100 text-teal-700',
    btn: 'bg-teal-500 hover:bg-teal-400 text-white',
    tag: 'bg-teal-50 text-teal-600 border border-teal-100',
    accent: 'text-teal-600',
  },
  sky: {
    badge: 'bg-sky-100 text-sky-700',
    btn: 'bg-sky-500 hover:bg-sky-400 text-white',
    tag: 'bg-sky-50 text-sky-600 border border-sky-100',
    accent: 'text-sky-600',
  },
  rose: {
    badge: 'bg-rose-100 text-rose-700',
    btn: 'bg-rose-400 hover:bg-rose-300 text-white',
    tag: 'bg-rose-50 text-rose-600 border border-rose-100',
    accent: 'text-rose-500',
  },
  amber: {
    badge: 'bg-amber-100 text-amber-700',
    btn: 'bg-amber-500 hover:bg-amber-400 text-white',
    tag: 'bg-amber-50 text-amber-600 border border-amber-100',
    accent: 'text-amber-600',
  },
  violet: {
    badge: 'bg-violet-100 text-violet-700',
    btn: 'bg-violet-500 hover:bg-violet-400 text-white',
    tag: 'bg-violet-50 text-violet-600 border border-violet-100',
    accent: 'text-violet-600',
  },
  emerald: {
    badge: 'bg-emerald-100 text-emerald-700',
    btn: 'bg-emerald-500 hover:bg-emerald-400 text-white',
    tag: 'bg-emerald-50 text-emerald-600 border border-emerald-100',
    accent: 'text-emerald-600',
  },
  indigo: {
    badge: 'bg-indigo-100 text-indigo-700',
    btn: 'bg-indigo-500 hover:bg-indigo-400 text-white',
    tag: 'bg-indigo-50 text-indigo-600 border border-indigo-100',
    accent: 'text-indigo-600',
  },
  orange: {
    badge: 'bg-orange-100 text-orange-700',
    btn: 'bg-orange-500 hover:bg-orange-400 text-white',
    tag: 'bg-orange-50 text-orange-600 border border-orange-100',
    accent: 'text-orange-600',
  },
  pink: {
    badge: 'bg-pink-100 text-pink-700',
    btn: 'bg-pink-500 hover:bg-pink-400 text-white',
    tag: 'bg-pink-50 text-pink-600 border border-pink-100',
    accent: 'text-pink-600',
  },
  cyan: {
    badge: 'bg-cyan-100 text-cyan-700',
    btn: 'bg-cyan-500 hover:bg-cyan-400 text-white',
    tag: 'bg-cyan-50 text-cyan-600 border border-cyan-100',
    accent: 'text-cyan-600',
  },
  lime: {
    badge: 'bg-lime-100 text-lime-700',
    btn: 'bg-lime-500 hover:bg-lime-400 text-white',
    tag: 'bg-lime-50 text-lime-600 border border-lime-100',
    accent: 'text-lime-600',
  },
};

export default function DormitoryPage() {
  const { dormitories, toggleRoomStatus, getAvailableCount, isFullyOccupied } = useDormitory();

  const [isScrolled, setIsScrolled] = useState(false);
  const [applyModal, setApplyModal] = useState<{ open: boolean; dormName: string; rooms: DormRoom[] }>({
    open: false,
    dormName: '',
    rooms: [],
  });
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [detailModal, setDetailModal] = useState<Dormitory | null>(null);
  const [manageModal, setManageModal] = useState<Dormitory | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  // ── フィルター状態 ──
  const [searchText, setSearchText] = useState('');
  const [filterArea, setFilterArea] = useState('all');
  const [filterRent, setFilterRent] = useState('all');
  const [filterVacancy, setFilterVacancy] = useState('all');
  const [filterGender, setFilterGender] = useState('all');
  const [sortOrder, setSortOrder] = useState<'default' | 'walk_asc' | 'walk_desc'>('default');

  // 徒歩分数を抽出するヘルパー
  const extractWalkMinutes = (access: string): number => {
    const match = access.match(/徒歩(\d+)分/);
    return match ? parseInt(match[1], 10) : 99;
  };

  // エリア一覧を動的生成
  const areaOptions = useMemo(() => {
    const areas = new Set<string>();
    dormitories.forEach((d) => {
      const match = d.address.match(/板橋区(.+)/);
      if (match) areas.add(match[1]);
    });
    return Array.from(areas).sort();
  }, [dormitories]);

  // フィルター適用
  const filteredDormitories = useMemo(() => {
    const filtered = dormitories.filter((dorm) => {
      // テキスト検索
      if (searchText) {
        const q = searchText.toLowerCase();
        const hit =
          dorm.name.toLowerCase().includes(q) ||
          dorm.address.toLowerCase().includes(q) ||
          dorm.access.toLowerCase().includes(q) ||
          dorm.tags.some((t) => t.toLowerCase().includes(q));
        if (!hit) return false;
      }
      // エリア
      if (filterArea !== 'all') {
        if (!dorm.address.includes(filterArea)) return false;
      }
      // 家賃（最低家賃で判定）
      if (filterRent !== 'all') {
        const minRent = Math.min(...dorm.rooms.map((r) => r.rent));
        if (filterRent === 'under30' && minRent > 30) return false;
        if (filterRent === '30to40' && (minRent < 30 || minRent > 40)) return false;
        if (filterRent === 'over40' && minRent < 40) return false;
      }
      // 空室状況
      if (filterVacancy === 'available' && isFullyOccupied(dorm)) return false;
      if (filterVacancy === 'occupied' && !isFullyOccupied(dorm)) return false;
      // 入居対象
      if (filterGender !== 'all') {
        if (filterGender === 'male' && dorm.gender !== '男性専用') return false;
        if (filterGender === 'female' && dorm.gender !== '女性専用') return false;
        if (filterGender === 'mixed' && dorm.gender !== '混合') return false;
      }
      return true;
    });

    // ソート
    if (sortOrder === 'walk_asc') {
      filtered.sort((a, b) => extractWalkMinutes(a.access) - extractWalkMinutes(b.access));
    } else if (sortOrder === 'walk_desc') {
      filtered.sort((a, b) => extractWalkMinutes(b.access) - extractWalkMinutes(a.access));
    }

    return filtered;
  }, [dormitories, searchText, filterArea, filterRent, filterVacancy, filterGender, sortOrder, isFullyOccupied]);

  const isFiltered =
    searchText !== '' ||
    filterArea !== 'all' ||
    filterRent !== 'all' ||
    filterVacancy !== 'all' ||
    filterGender !== 'all' ||
    sortOrder !== 'default';

  const resetFilters = () => {
    setSearchText('');
    setFilterArea('all');
    setFilterRent('all');
    setFilterVacancy('all');
    setFilterGender('all');
    setSortOrder('default');
  };

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const syncedManageModal = manageModal
    ? dormitories.find((d) => d.id === manageModal.id) ?? null
    : null;
  const syncedDetailModal = detailModal
    ? dormitories.find((d) => d.id === detailModal.id) ?? null
    : null;

  const openApply = (dorm: Dormitory) => {
    setApplyModal({ open: true, dormName: dorm.name, rooms: dorm.rooms });
    setSubmitStatus('idle');
  };

  const closeApply = () => {
    setApplyModal({ open: false, dormName: '', rooms: [] });
    setSubmitStatus('idle');
    formRef.current?.reset();
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitStatus('sending');
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      const res = await fetch('https://readdy.ai/api/form/d6ppr05sbgrc47filr3g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitStatus(res.ok ? 'success' : 'error');
    } catch {
      setSubmitStatus('error');
    }
  };

  const applyRooms = applyModal.open
    ? (dormitories.find((d) => d.name === applyModal.dormName)?.rooms ?? applyModal.rooms)
    : [];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md' : 'bg-white shadow-sm'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-2 cursor-pointer">
            <img
              src="https://static.readdy.ai/image/b2ad99a010827991956f87b3b8c8a32e/4f1bfceebacf762bbb456cb39f739307.png"
              alt="虹橋株式会社"
              className="h-10 w-auto object-contain"
            />
          </Link>
          <div className="flex items-center space-x-8">
            <Link to="/#about" className="text-sm font-medium text-slate-700 hover:text-teal-500 transition-colors whitespace-nowrap cursor-pointer">企業情報</Link>
            <Link to="/#services" className="text-sm font-medium text-slate-700 hover:text-teal-500 transition-colors whitespace-nowrap cursor-pointer">サービス</Link>
            <Link to="/dormitory" className="text-sm font-medium text-teal-600 border-b border-teal-500 whitespace-nowrap cursor-pointer">宿舎管理</Link>
            <Link to="/downloads" className="text-sm font-medium text-slate-700 hover:text-teal-500 transition-colors whitespace-nowrap cursor-pointer">資料ダウンロード</Link>
            <Link to="/contact" className="text-sm font-medium text-slate-700 hover:text-teal-500 transition-colors whitespace-nowrap cursor-pointer">お問い合わせ</Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-50 via-white to-sky-50"></div>
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <span className="inline-block text-xs font-light tracking-widest text-teal-500 mb-4 uppercase">Dormitory</span>
          <h1 className="text-4xl lg:text-5xl font-light text-slate-800 mb-5" style={{ fontFamily: 'serif' }}>
            宿舎管理
          </h1>
          <p className="text-sm text-slate-500 leading-relaxed">
            虹橋株式会社が管理する学生宿舎をご紹介します。<br />
            安心・安全な住環境で、充実した学生生活をサポートします。
          </p>
        </div>

        {/* Stats */}
        <div className="relative max-w-3xl mx-auto px-6 mt-12 grid grid-cols-3 gap-6">
          {[
            { icon: 'ri-building-4-line', value: `${dormitories.length}棟`, label: '管理物件数' },
            {
              icon: 'ri-door-open-line',
              value: `${dormitories.reduce((s, d) => s + getAvailableCount(d), 0)}室`,
              label: '空室数',
            },
            { icon: 'ri-map-pin-line', value: '東京都板橋区', label: '管理エリア' },
          ].map((stat) => (
            <div key={stat.label} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 text-center">
              <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3 bg-teal-50 rounded-xl text-teal-500">
                <i className={`${stat.icon} text-xl`}></i>
              </div>
              <p className="text-2xl font-semibold text-slate-800 mb-1">{stat.value}</p>
              <p className="text-xs text-slate-400">{stat.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Search & Filter Bar ── */}
      <section className="sticky top-[72px] z-40 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-6xl mx-auto px-6 py-4">
          {/* 検索ボックス */}
          <div className="flex items-center gap-3 mb-3">
            <div className="relative flex-1 max-w-sm">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm"></i>
              <input
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="物件名・エリア・設備で検索..."
                className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-teal-400 transition-colors"
              />
              {searchText && (
                <button
                  onClick={() => setSearchText('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer"
                >
                  <i className="ri-close-line text-sm"></i>
                </button>
              )}
            </div>

            {/* 並び替え */}
            <div className="flex items-center gap-1.5">
              <i className="ri-sort-asc text-xs text-slate-400"></i>
              <span className="text-xs text-slate-400 whitespace-nowrap">並び替え</span>
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value as 'default' | 'walk_asc' | 'walk_desc')}
                className={`text-xs border rounded-full px-3 py-1.5 focus:outline-none focus:border-teal-400 transition-colors cursor-pointer whitespace-nowrap ${
                  sortOrder !== 'default'
                    ? 'border-teal-400 bg-teal-50 text-teal-700 font-medium'
                    : 'border-gray-200 bg-white text-slate-600'
                }`}
              >
                <option value="default">デフォルト</option>
                <option value="walk_asc">徒歩分数 近い順</option>
                <option value="walk_desc">徒歩分数 遠い順</option>
              </select>
            </div>

            {/* 結果件数 & リセット */}
            <div className="flex items-center gap-2 ml-auto">
              <span className="text-xs text-slate-500 whitespace-nowrap">
                <span className="font-semibold text-teal-600">{filteredDormitories.length}</span>
                <span> / {dormitories.length} 件</span>
              </span>
              {isFiltered && (
                <button
                  onClick={resetFilters}
                  className="flex items-center space-x-1 px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 text-xs transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-refresh-line text-xs"></i>
                  <span>リセット</span>
                </button>
              )}
            </div>
          </div>

          {/* フィルターチップ */}
          <div className="flex items-center gap-2 flex-wrap">
            {/* エリア */}
            <div className="flex items-center gap-1.5">
              <i className="ri-map-pin-2-line text-xs text-slate-400"></i>
              <span className="text-xs text-slate-400 whitespace-nowrap">エリア</span>
              <select
                value={filterArea}
                onChange={(e) => setFilterArea(e.target.value)}
                className={`text-xs border rounded-full px-3 py-1.5 focus:outline-none focus:border-teal-400 transition-colors cursor-pointer whitespace-nowrap ${
                  filterArea !== 'all'
                    ? 'border-teal-400 bg-teal-50 text-teal-700 font-medium'
                    : 'border-gray-200 bg-white text-slate-600'
                }`}
              >
                <option value="all">すべて</option>
                {areaOptions.map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
            </div>

            <div className="w-px h-4 bg-gray-200"></div>

            {/* 家賃 */}
            <div className="flex items-center gap-1.5">
              <i className="ri-money-yen-circle-line text-xs text-slate-400"></i>
              <span className="text-xs text-slate-400 whitespace-nowrap">家賃</span>
              {[
                { value: 'all', label: 'すべて' },
                { value: 'under30', label: '30万以下' },
                { value: '30to40', label: '30〜40万' },
                { value: 'over40', label: '40万以上' },
              ].map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setFilterRent(opt.value)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors cursor-pointer whitespace-nowrap ${
                    filterRent === opt.value
                      ? 'border-teal-400 bg-teal-50 text-teal-700 font-medium'
                      : 'border-gray-200 bg-white text-slate-600 hover:border-gray-300'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            <div className="w-px h-4 bg-gray-200"></div>

            {/* 空室状況 */}
            <div className="flex items-center gap-1.5">
              <i className="ri-door-open-line text-xs text-slate-400"></i>
              <span className="text-xs text-slate-400 whitespace-nowrap">空室</span>
              {[
                { value: 'all', label: 'すべて' },
                { value: 'available', label: '空室あり' },
                { value: 'occupied', label: '満室のみ' },
              ].map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setFilterVacancy(opt.value)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors cursor-pointer whitespace-nowrap ${
                    filterVacancy === opt.value
                      ? opt.value === 'available'
                        ? 'border-green-400 bg-green-50 text-green-700 font-medium'
                        : opt.value === 'occupied'
                        ? 'border-slate-400 bg-slate-100 text-slate-700 font-medium'
                        : 'border-teal-400 bg-teal-50 text-teal-700 font-medium'
                      : 'border-gray-200 bg-white text-slate-600 hover:border-gray-300'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            <div className="w-px h-4 bg-gray-200"></div>

            {/* 入居対象 */}
            <div className="flex items-center gap-1.5">
              <i className="ri-group-line text-xs text-slate-400"></i>
              <span className="text-xs text-slate-400 whitespace-nowrap">対象</span>
              {[
                { value: 'all', label: 'すべて' },
                { value: 'mixed', label: '混合' },
                { value: 'female', label: '女性専用' },
                { value: 'male', label: '男性専用' },
              ].map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setFilterGender(opt.value)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors cursor-pointer whitespace-nowrap ${
                    filterGender === opt.value
                      ? opt.value === 'female'
                        ? 'border-rose-400 bg-rose-50 text-rose-700 font-medium'
                        : opt.value === 'male'
                        ? 'border-sky-400 bg-sky-50 text-sky-700 font-medium'
                        : 'border-teal-400 bg-teal-50 text-teal-700 font-medium'
                      : 'border-gray-200 bg-white text-slate-600 hover:border-gray-300'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Dormitory Cards */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          {filteredDormitories.length === 0 ? (
            <div className="text-center py-24">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-slate-100 rounded-full text-slate-400">
                <i className="ri-search-line text-2xl"></i>
              </div>
              <p className="text-slate-500 text-sm mb-2">条件に一致する物件が見つかりませんでした</p>
              <p className="text-slate-400 text-xs mb-6">フィルターを変更してお試しください</p>
              <button
                onClick={resetFilters}
                className="px-5 py-2.5 bg-teal-500 text-white rounded-lg text-sm hover:bg-teal-400 transition-colors cursor-pointer whitespace-nowrap"
              >
                フィルターをリセット
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {filteredDormitories.map((dorm) => {
                const c = colorMap[dorm.color];
                const availableCount = getAvailableCount(dorm);
                const fullyOccupied = isFullyOccupied(dorm);
                const walkMin = extractWalkMinutes(dorm.access);
                return (
                  <div
                    key={dorm.id}
                    className="bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 group flex flex-col"
                  >
                    <div className="relative w-full h-52 overflow-hidden">
                      <img
                        src={dorm.image}
                        alt={dorm.name}
                        className={`w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500 ${fullyOccupied ? 'grayscale-[30%]' : ''}`}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                      <div className="absolute bottom-3 left-3 flex items-center gap-2 flex-wrap">
                        <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${c.badge}`}>
                          {dorm.gender}
                        </span>
                        {fullyOccupied ? (
                          <span className="text-xs px-2.5 py-1 rounded-full font-medium bg-slate-700 text-white">満室</span>
                        ) : (
                          <span className="text-xs px-2.5 py-1 rounded-full font-medium bg-green-500 text-white">空室あり</span>
                        )}
                      </div>
                      {/* 徒歩分数バッジ */}
                      {walkMin < 99 && (
                        <div className="absolute top-3 left-3 flex items-center space-x-1 px-2.5 py-1 rounded-full bg-white/90 backdrop-blur-sm text-slate-700 text-xs font-medium shadow-sm">
                          <i className="ri-walk-line text-teal-500"></i>
                          <span>徒歩{walkMin}分</span>
                        </div>
                      )}
                      <button
                        onClick={() => setManageModal(dorm)}
                        className="absolute top-3 right-3 flex items-center space-x-1 px-2.5 py-1.5 rounded-lg bg-black/40 hover:bg-black/60 text-white text-xs transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-settings-3-line text-sm"></i>
                        <span>管理</span>
                      </button>
                    </div>

                    <div className="p-6 flex flex-col flex-1">
                      <div className="mb-4 flex items-start justify-between">
                        <div>
                          <h2 className="text-base font-semibold text-slate-800 mb-0.5">{dorm.name}</h2>
                          <p className={`text-xs font-medium ${c.accent}`}>{dorm.subtitle}</p>
                        </div>
                        <div className="text-right shrink-0 ml-2">
                          <p className={`text-lg font-bold ${fullyOccupied ? 'text-slate-400' : 'text-green-500'}`}>
                            {availableCount}<span className="text-xs font-normal ml-0.5">室</span>
                          </p>
                          <p className="text-xs text-slate-400">空室</p>
                        </div>
                      </div>

                      <div className="space-y-1.5 mb-4">
                        <div className="flex items-start space-x-2 text-xs text-slate-500">
                          <i className="ri-map-pin-line mt-0.5 shrink-0 text-slate-400"></i>
                          <span>{dorm.address}</span>
                        </div>
                        <div className="flex items-start space-x-2 text-xs text-slate-500">
                          <i className="ri-train-line mt-0.5 shrink-0 text-slate-400"></i>
                          <span>{dorm.access}</span>
                        </div>
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(dorm.address)}`}
                          target="_blank"
                          rel="nofollow noopener noreferrer"
                          className="inline-flex items-center space-x-1.5 text-xs text-teal-600 hover:text-teal-500 transition-colors cursor-pointer mt-0.5"
                        >
                          <i className="ri-map-2-line text-sm"></i>
                          <span>Google Maps で確認</span>
                          <i className="ri-external-link-line text-xs opacity-70"></i>
                        </a>
                      </div>

                      <div className="flex flex-wrap gap-1.5 mb-5">
                        {dorm.tags.map((tag) => (
                          <span key={tag} className={`text-xs px-2 py-0.5 rounded-full ${c.tag}`}>{tag}</span>
                        ))}
                      </div>

                      <div className="rounded-xl overflow-hidden border border-gray-100 mb-5 text-xs">
                        <div className={`px-3 py-2 font-semibold ${c.badge} flex items-center justify-between`}>
                          <span>部屋別空室状況</span>
                          <span className={`text-xs font-normal ${fullyOccupied ? 'text-slate-500' : 'text-green-600'}`}>
                            {availableCount}/{dorm.rooms.length} 空室
                          </span>
                        </div>
                        <table className="w-full text-slate-600">
                          <thead>
                            <tr className="bg-gray-50 text-slate-400">
                              <th className="px-3 py-1.5 text-left font-medium">部屋</th>
                              <th className="px-3 py-1.5 text-center font-medium">房租/人</th>
                              <th className="px-3 py-1.5 text-center font-medium">合計</th>
                              <th className="px-3 py-1.5 text-center font-medium">状況</th>
                            </tr>
                          </thead>
                          <tbody>
                            {dorm.rooms.map((room) => (
                              <tr key={room.name} className="border-t border-gray-100">
                                <td className="px-3 py-1.5">{room.name}</td>
                                <td className="px-3 py-1.5 text-center">{room.rent}万</td>
                                <td className={`px-3 py-1.5 text-center font-semibold ${c.accent}`}>{room.total}万</td>
                                <td className="px-3 py-1.5 text-center">
                                  {room.status === 'available' ? (
                                    <span className="inline-flex items-center space-x-0.5 px-2 py-0.5 rounded-full bg-green-50 text-green-600 border border-green-100">
                                      <i className="ri-checkbox-circle-line text-xs"></i>
                                      <span>空室</span>
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center space-x-0.5 px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200">
                                      <i className="ri-close-circle-line text-xs"></i>
                                      <span>満室</span>
                                    </span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <div className={`px-3 py-2 text-xs ${c.badge} flex items-center space-x-1`}>
                          <i className="ri-information-line shrink-0"></i>
                          <span>{dorm.note}</span>
                        </div>
                      </div>

                      <div className="mt-auto flex items-center gap-2">
                        <button
                          onClick={() => setDetailModal(dorm)}
                          className="flex-1 py-2.5 border border-gray-200 rounded-lg text-xs font-medium text-slate-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-eye-line mr-1"></i>詳細を見る
                        </button>
                        <button
                          onClick={() => openApply(dorm)}
                          disabled={fullyOccupied}
                          className={`flex-1 py-2.5 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                            fullyOccupied ? 'bg-slate-200 text-slate-400 cursor-not-allowed' : c.btn
                          }`}
                        >
                          <i className="ri-edit-line mr-1"></i>
                          {fullyOccupied ? '満室' : '入居申込'}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Info Section */}
      <section className="py-16 bg-gradient-to-br from-teal-50 to-sky-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-xs tracking-widest text-teal-500 uppercase">Rules &amp; Support</span>
            <h2 className="text-2xl font-light text-slate-800 mt-2" style={{ fontFamily: 'serif' }}>入居ルール・サポート</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: 'ri-shield-check-line', title: '安心の管理体制', desc: '専任スタッフが日本語・中国語で対応。緊急時も迅速にサポートします。', color: 'teal' },
              { icon: 'ri-calendar-check-line', title: '費用の支払い', desc: '入居時に入居費＋6ヶ月分を一括納付。以後3ヶ月ごとに前月末までに納付。', color: 'sky' },
              { icon: 'ri-group-line', title: '共用部の清掃', desc: 'キッチン・洗面台・浴室・トイレは2週間ごとに入居者が輪番で清掃します。', color: 'teal' },
            ].map((item) => (
              <div key={item.title} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <div className={`w-11 h-11 flex items-center justify-center rounded-xl mb-4 ${item.color === 'teal' ? 'bg-teal-100 text-teal-600' : 'bg-sky-100 text-sky-600'}`}>
                  <i className={`${item.icon} text-xl`}></i>
                </div>
                <h3 className="text-sm font-semibold text-slate-800 mb-2">{item.title}</h3>
                <p className="text-xs text-slate-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-2xl font-light text-slate-800 mb-3" style={{ fontFamily: 'serif' }}>
            ご不明な点はお気軽にご相談ください
          </h2>
          <p className="text-sm text-slate-500 mb-8">
            宿舎に関するご質問・内覧のご希望など、担当者が丁寧にご対応いたします。
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center space-x-2 bg-teal-500 text-white px-8 py-3.5 rounded-lg text-sm hover:bg-teal-400 transition-colors whitespace-nowrap cursor-pointer"
          >
            <span>お問い合わせはこちら</span>
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-800 text-white py-8">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
          <div className="flex items-center space-x-2">
            <img
              src="https://static.readdy.ai/image/b2ad99a010827991956f87b3b8c8a32e/4f1bfceebacf762bbb456cb39f739307.png"
              alt="虹橋株式会社"
              className="h-8 w-auto object-contain brightness-0 invert"
            />
            <span className="font-bold text-base">虹橋株式会社</span>
          </div>
          <p className="text-xs text-gray-400">© 虹橋株式会社 2025 All rights reserved</p>
        </div>
      </footer>

      {/* Manage Status Modal */}
      {syncedManageModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
              <div className="flex items-center space-x-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-teal-100 text-teal-600">
                  <i className="ri-settings-3-line text-lg"></i>
                </div>
                <div>
                  <h2 className="text-base font-semibold text-slate-800">空室ステータス管理</h2>
                  <p className="text-xs text-slate-400 mt-0.5">{syncedManageModal.name}</p>
                </div>
              </div>
              <button
                onClick={() => setManageModal(null)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-slate-400 cursor-pointer transition-colors"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <div className="px-6 py-5 space-y-3">
              <p className="text-xs text-slate-400 mb-4">各部屋のステータスをクリックして切り替えてください。</p>
              {syncedManageModal.rooms.map((room) => (
                <div
                  key={room.name}
                  className="flex items-center justify-between p-4 rounded-xl border border-gray-100 hover:border-teal-200 transition-colors"
                >
                  <div>
                    <p className="text-sm font-medium text-slate-700">{room.name}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{room.type} ／ {room.rent}万円/人（6ヶ月）</p>
                  </div>
                  <button
                    onClick={() => toggleRoomStatus(syncedManageModal.id, room.name)}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                      room.status === 'available'
                        ? 'bg-green-100 text-green-700 hover:bg-green-200'
                        : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                    }`}
                  >
                    <span className={`w-2 h-2 rounded-full ${room.status === 'available' ? 'bg-green-500' : 'bg-slate-400'}`}></span>
                    <span>{room.status === 'available' ? '空室' : '満室'}</span>
                    <i className="ri-refresh-line text-xs opacity-60"></i>
                  </button>
                </div>
              ))}
              <div className="pt-3 border-t border-gray-100 flex items-center justify-between text-xs text-slate-400">
                <span>
                  空室: {syncedManageModal.rooms.filter((r) => r.status === 'available').length}室 ／
                  満室: {syncedManageModal.rooms.filter((r) => r.status === 'occupied').length}室
                </span>
                <button
                  onClick={() => setManageModal(null)}
                  className="px-4 py-2 bg-teal-500 text-white rounded-lg text-xs font-medium hover:bg-teal-400 transition-colors cursor-pointer whitespace-nowrap"
                >
                  完了
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {syncedDetailModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
            <div className="relative w-full h-56 overflow-hidden rounded-t-2xl">
              <img src={syncedDetailModal.image} alt={syncedDetailModal.name} className="w-full h-full object-cover object-top" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
              <button
                onClick={() => setDetailModal(null)}
                className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-black/30 text-white hover:bg-black/50 cursor-pointer transition-colors"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
              <div className="absolute bottom-4 left-4">
                <h2 className="text-lg font-semibold text-white">{syncedDetailModal.name}</h2>
                <p className="text-xs text-white/80">{syncedDetailModal.subtitle}</p>
              </div>
              <div className="absolute bottom-4 right-4">
                {isFullyOccupied(syncedDetailModal) ? (
                  <span className="text-xs px-2.5 py-1 rounded-full font-medium bg-slate-700 text-white">満室</span>
                ) : (
                  <span className="text-xs px-2.5 py-1 rounded-full font-medium bg-green-500 text-white">
                    空室あり {getAvailableCount(syncedDetailModal)}室
                  </span>
                )}
              </div>
            </div>
            <div className="p-6 space-y-5">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-xs text-slate-400 mb-1">所在地</p>
                  <p className="text-slate-700">{syncedDetailModal.address}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400 mb-1">アクセス</p>
                  <p className="text-slate-700">{syncedDetailModal.access}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400 mb-1">入居対象</p>
                  <p className="text-slate-700">{syncedDetailModal.gender}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400 mb-1">定員</p>
                  <p className="text-slate-700">{syncedDetailModal.capacity}名</p>
                </div>
              </div>

              <div>
                <p className="text-xs text-slate-400 mb-2">設備</p>
                <div className="flex flex-wrap gap-2">
                  {syncedDetailModal.features.map((f) => (
                    <span key={f} className="text-xs px-2.5 py-1 rounded-full bg-teal-50 text-teal-600 border border-teal-100">{f}</span>
                  ))}
                </div>
              </div>

              <div className="rounded-xl overflow-hidden border border-gray-100 text-xs">
                <div className="bg-teal-50 px-3 py-2 font-semibold text-teal-700 flex items-center justify-between">
                  <span>部屋別空室状況</span>
                  <span className={`text-xs font-normal ${isFullyOccupied(syncedDetailModal) ? 'text-slate-500' : 'text-green-600'}`}>
                    {getAvailableCount(syncedDetailModal)}/{syncedDetailModal.rooms.length} 空室
                  </span>
                </div>
                <table className="w-full text-slate-600">
                  <thead>
                    <tr className="bg-gray-50 text-slate-400">
                      <th className="px-3 py-1.5 text-left font-medium">部屋</th>
                      <th className="px-3 py-1.5 text-center font-medium">房租/人</th>
                      <th className="px-3 py-1.5 text-center font-medium">合計</th>
                      <th className="px-3 py-1.5 text-center font-medium">状況</th>
                    </tr>
                  </thead>
                  <tbody>
                    {syncedDetailModal.rooms.map((room) => (
                      <tr key={room.name} className="border-t border-gray-100">
                        <td className="px-3 py-1.5">{room.name}</td>
                        <td className="px-3 py-1.5 text-center">{room.rent}万</td>
                        <td className="px-3 py-1.5 text-center font-semibold text-teal-600">{room.total}万</td>
                        <td className="px-3 py-1.5 text-center">
                          {room.status === 'available' ? (
                            <span className="inline-flex items-center space-x-0.5 px-2 py-0.5 rounded-full bg-green-50 text-green-600 border border-green-100">
                              <i className="ri-checkbox-circle-line text-xs"></i>
                              <span>空室</span>
                            </span>
                          ) : (
                            <span className="inline-flex items-center space-x-0.5 px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200">
                              <i className="ri-close-circle-line text-xs"></i>
                              <span>満室</span>
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="bg-teal-50/60 px-3 py-2 text-teal-700 flex items-center space-x-1">
                  <i className="ri-information-line"></i>
                  <span>{syncedDetailModal.note}</span>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setDetailModal(null)}
                  className="flex-1 py-2.5 border border-gray-200 rounded-lg text-sm text-slate-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  閉じる
                </button>
                <button
                  onClick={() => { setDetailModal(null); openApply(syncedDetailModal); }}
                  disabled={isFullyOccupied(syncedDetailModal)}
                  className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    isFullyOccupied(syncedDetailModal)
                      ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                      : 'bg-teal-500 text-white hover:bg-teal-400'
                  }`}
                >
                  <i className="ri-edit-line mr-1"></i>
                  {isFullyOccupied(syncedDetailModal) ? '満室' : '入居申込'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Apply Modal */}
      {applyModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
              <div className="flex items-center space-x-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-teal-100 text-teal-600">
                  <i className="ri-home-smile-line text-lg"></i>
                </div>
                <div>
                  <h2 className="text-base font-semibold text-slate-800">入居申込フォーム</h2>
                  <p className="text-xs text-slate-400 mt-0.5">{applyModal.dormName}</p>
                </div>
              </div>
              <button
                onClick={closeApply}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-slate-400 cursor-pointer transition-colors"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            <div className="px-6 py-6">
              {submitStatus === 'success' ? (
                <div className="text-center py-10">
                  <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-teal-100 rounded-full text-teal-500">
                    <i className="ri-check-double-line text-3xl"></i>
                  </div>
                  <h3 className="text-base font-semibold text-slate-800 mb-2">申込を受け付けました</h3>
                  <p className="text-sm text-slate-500 mb-6">担当者より3営業日以内にご連絡いたします。</p>
                  <button
                    onClick={closeApply}
                    className="px-6 py-2.5 bg-teal-500 text-white rounded-lg text-sm hover:bg-teal-400 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    閉じる
                  </button>
                </div>
              ) : (
                <form ref={formRef} onSubmit={handleSubmit} data-readdy-form className="space-y-4">
                  <input type="hidden" name="物件名" value={applyModal.dormName} />

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1.5">お名前 <span className="text-red-400">*</span></label>
                      <input type="text" name="お名前" required placeholder="山田 太郎"
                        className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-teal-400 transition-colors" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1.5">フリガナ</label>
                      <input type="text" name="フリガナ" placeholder="ヤマダ タロウ"
                        className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-teal-400 transition-colors" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1.5">メールアドレス <span className="text-red-400">*</span></label>
                    <input type="email" name="email" required placeholder="example@email.com"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-teal-400 transition-colors" />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1.5">電話番号 <span className="text-red-400">*</span></label>
                    <input type="tel" name="電話番号" required placeholder="090-0000-0000"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-teal-400 transition-colors" />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1.5">国籍</label>
                      <input type="text" name="国籍" placeholder="中国"
                        className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-teal-400 transition-colors" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1.5">在留資格</label>
                      <select name="在留資格"
                        className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-teal-400 transition-colors cursor-pointer">
                        <option value="">選択してください</option>
                        <option value="留学">留学</option>
                        <option value="特定活動">特定活動</option>
                        <option value="技術・人文知識・国際業務">技術・人文知識・国際業務</option>
                        <option value="その他">その他</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1.5">入居希望日 <span className="text-red-400">*</span></label>
                      <input type="date" name="入居希望日" required
                        className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-teal-400 transition-colors cursor-pointer" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1.5">希望部屋</label>
                      <select name="希望部屋"
                        className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-teal-400 transition-colors cursor-pointer">
                        <option value="">指定なし</option>
                        {applyRooms
                          .filter((r) => r.status === 'available')
                          .map((r) => (
                            <option key={r.name} value={r.name}>{r.name}</option>
                          ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1.5">備考・ご質問</label>
                    <textarea name="備考" rows={3} maxLength={500}
                      placeholder="ご質問・ご要望があればご記入ください（500文字以内）"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-teal-400 transition-colors resize-none" />
                  </div>

                  {submitStatus === 'error' && (
                    <p className="text-xs text-red-500 flex items-center space-x-1">
                      <i className="ri-error-warning-line"></i>
                      <span>送信に失敗しました。時間をおいて再度お試しください。</span>
                    </p>
                  )}

                  <div className="flex items-center gap-3 pt-2">
                    <button type="button" onClick={closeApply}
                      className="flex-1 py-2.5 border border-gray-200 rounded-lg text-sm text-slate-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">
                      キャンセル
                    </button>
                    <button type="submit" disabled={submitStatus === 'sending'}
                      className="flex-1 py-2.5 bg-teal-500 text-white rounded-lg text-sm font-medium hover:bg-teal-400 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 flex items-center justify-center space-x-2">
                      {submitStatus === 'sending' ? (
                        <><i className="ri-loader-4-line animate-spin"></i><span>送信中...</span></>
                      ) : (
                        <><i className="ri-send-plane-line"></i><span>申込を送信する</span></>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
